import {
  defineComponent,
  ref,
  watch,
  nextTick,
  onBeforeUnmount,
  onMounted,
  PropType,
} from 'vue';
import { getStylesBinder } from '~mall/common/utils/styles';
import styles from './index.module.less';
import { MarketingWord } from '~mall/constants/bike';

const cx = getStylesBinder(styles);

export default defineComponent({
  name: 'UseNotice',
  props: {
    /** 标题 */
    title: {
      type: String,
      required: true,
    },
    /** 内容 */
    desc: {
      type: String,
      required: true,
    },
    /** 两轮卡场景描述 */
    twoWheelCardDescList: {
      type: Array as PropType<MarketingWord[]>,
      default: () => [],
    },
  },
  setup: (props) => {
    /** 是否展示”查看更多“ */
    const showMore = ref(false);
    /** 内容是否已展开 */
    const expanded = ref(false);
    const currDesc = ref('');
    const containerRef = ref<HTMLElement | null>(null);
    const anchorRef = ref<HTMLElement | null>(null);
    /** 两轮卡场景描述 */
    const currMarketingWords = ref<MarketingWord[]>([]);
    /** 监听内容是否溢出并需要展示”查看更多“ */
    let _observer: IntersectionObserver | null = null;
    onMounted(() => {
      expanded.value = true;
    });
    /** 点击查看更多 */
    const handleShowMore = () => {
      expanded.value = !expanded.value;
    };
    watch(expanded, (value) => { containerRef.value?.classList.toggle('auto', value); });

    const teardownObserver = () => {
      if (_observer) {
        _observer.disconnect();
        _observer = null;
      }
    };
    const setupObserver = () => {
      teardownObserver();

      if (containerRef.value && anchorRef.value) {
        _observer = new IntersectionObserver((entries) => {
          entries.forEach(({ isIntersecting }) => {
            if (!isIntersecting) {
              teardownObserver();
              showMore.value = true;
            }
          });
        }, {
          root: containerRef.value,
        });
        _observer.observe(anchorRef.value);
      }
    };

    onBeforeUnmount(() => {
      teardownObserver();
    });

    watch(() => props.desc, (newVal) => {
      if (newVal) {
        nextTick(() => {
          setupObserver();
          currDesc.value = newVal;
        });
      }
    }, { immediate: true });

    watch(() => props.twoWheelCardDescList, (newVal) => {
      if (newVal) {
        const descList = newVal
          .filter((item) => [3, 8].includes(item.wordsType) && item.wordsDescExt);
        currMarketingWords.value = descList;
      }
    }, { immediate: true });

    return () => {
      if (!props.desc) {
        return null;
      }

      return (
        <div class={cx('notice')}>
          <div class={cx('notice__title')}>{props.title}</div>
          <div
            ref={containerRef}
            class={cx('notice__desc')}
          >
            {
              currMarketingWords.value.length > 0 ? currMarketingWords.value.map((item) => (
                <div class={cx('notice__desc-item')}>
                  <span class={cx('notice__desc-item-value')}>{item.wordsValue}</span>
                  <span class={cx('notice__desc-item-desc')}>/ {item.wordsDescExt}</span>
                </div>
              )) : (
                <div domPropsInnerHTML={currDesc.value}></div>
              )
            }
            <div ref={anchorRef} class={cx('notice__desc-anchor')} />
          </div>
          {showMore.value && !expanded.value && (
            <div class={cx('notice__more')} onClick={handleShowMore}>
              <span class={cx('notice__more-text')}>查看更多</span><van-icon
                name="arrow-down"
                class={cx('notice__more-icon')}
              />
            </div>
          )}
        </div>
      );
    };
  },
});
